import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

// Persistent wishlist (favoritos): a set of activity ids kept in localStorage so
// it survives reloads and works for anonymous visitors — no login required.
const WISHLIST_STORAGE_KEY = 'owa_wishlist';
const WishlistContext = createContext(null);

function readInitial() {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(WISHLIST_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((x) => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

export function WishlistProvider({ children }) {
  const [ids, setIds] = useState(readInitial);

  useEffect(() => {
    try {
      window.localStorage.setItem(WISHLIST_STORAGE_KEY, JSON.stringify(ids));
    } catch {
      /* storage may be unavailable (private mode) — favourites stay in memory */
    }
  }, [ids]);

  // Keep multiple open tabs in sync.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === WISHLIST_STORAGE_KEY) setIds(readInitial());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const isSaved = useCallback((id) => ids.includes(id), [ids]);
  const toggle = useCallback((id) => {
    if (!id) return;
    setIds((current) => (current.includes(id) ? current.filter((x) => x !== id) : [id, ...current]));
  }, []);
  const remove = useCallback((id) => setIds((current) => current.filter((x) => x !== id)), []);
  const clear = useCallback(() => setIds([]), []);

  const value = useMemo(
    () => ({ ids, count: ids.length, isSaved, toggle, remove, clear }),
    [ids, isSaved, toggle, remove, clear]
  );

  return <WishlistContext.Provider value={value}>{children}</WishlistContext.Provider>;
}

export function useWishlist() {
  const context = useContext(WishlistContext);
  if (!context) throw new Error('useWishlist must be used within WishlistProvider');
  return context;
}
